<template>
  <div>
    <!-- <h2>Parcel Tracking</h2>
    <p>Tracking Number: {{ trackingNumber }}</p> -->
    <VBtn color="primary" flat="" @click="startTracking" class="my-2"
      >Current Location<VIcon
        color="white"
        icon="fa-map-marker"
        size="small"
      ></VIcon
    ></VBtn>
    <div id="map" style="width: 100%; height: 500px"></div>
  </div>
</template>

<script setup>
/* eslint-disable*/
import { ref, onMounted, onUnmounted, nextTick, computed } from "vue";
import mapboxgl from "mapbox-gl";
import axios from "axios";

const $props = defineProps({
  shipmentData: {
    type: Object,
    default: () => ({}),
  },
});

const shipment = computed(() => $props.shipmentData);

// Mapbox configuration
const mapboxAccessToken =
  "pk.eyJ1IjoibG9naXN1cmVkaXNwYXRjaCIsImEiOiJjbWViOXExaXgwenBpMmpxeHJzaWZ2am01In0.jMqxRUDnsAQTXRGb01Eq1Q";
mapboxgl.accessToken = mapboxAccessToken;

// State
const trackingNumber = ref("");
const map = ref(null);
const parcel = ref({
  id: "",
  status: "In Transit",
  address: "",
  estimatedDelivery: "2025-08-20",
  location: null, // { lat, lng }
});
const routeLayerId = "route";
const currentLocationIndex = ref(0);

// Predefined street addresses
const defaultStreetAddresses = [
  { name: "350 5th Ave, New York, NY 10118, USA", lat: null, lng: null }, // Empire State Building
  { name: "32 London Bridge St, London SE1 9SG, UK", lat: null, lng: null }, // The Shard
  // {
  //   name: "1-1-2 Shibakoen, Minato City, Tokyo 105-0011, Japan",
  //   lat: null,
  //   lng: null,
  // }, // Tokyo Tower
  // { name: "1 Martin Pl, Sydney NSW 2000, Australia", lat: null, lng: null }, // Sydney GPO
  // {
  //   name: "2 Jan Smuts Ave, Cape Town, 8001, South Africa",
  //   lat: null,
  //   lng: null,
  // }, // Cape Town City Hall
];

const streetAddresses = computed(() => {
  const userAddresses = [
    {
      name: shipment.value?.fromLocation,
      lat: null,
      lng: null,
    },
    { name: shipment.value?.location, lat: null, lng: null },
  ];

  return shipment.value.address || shipment.value.location
    ? userAddresses
    : defaultStreetAddresses;
});

// Generate a unique tracking number
const generateTrackingNumber = () => {
  const prefix = "PRJ";
  const random = Math.random().toString(36).substr(2, 9).toUpperCase();
  return `${prefix}${random}`;
};

// Geocode address to coordinates
const geocodeAddress = async (address) => {
  try {
    const response = await axios.get(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        address
      )}.json?access_token=${mapboxAccessToken}`
    );
    const [lng, lat] = response.data.features[0].center;
    return { lat, lng };
  } catch (error) {
    console.error(`Error geocoding ${address}:`, error);
    return null;
  }
};

// Initialize coordinates for all addresses
const initializeCoordinates = async () => {
  for (const location of streetAddresses.value) {
    if (!location.lat || !location.lng) {
      const coords = await geocodeAddress(location.name);
      if (coords) {
        location.lat = coords?.lat;
        location.lng = coords?.lng;
      }
    }
  }
};

// Initialize Mapbox map
onMounted(async () => {
  // Ensure DOM is fully rendered
  await nextTick();

  // Check if map container exists
  const mapContainer = document.getElementById("map");
  if (!mapContainer) {
    console.error("Map container not found");
    return;
  }

  // Initialize map
  map.value = new mapboxgl.Map({
    container: "map",
    style: "mapbox://styles/logisuredispatch/cmebfporr003d01s6aojig1ke",
    center: [
      streetAddresses.value[0].lng || -74.006,
      streetAddresses.value[0].lat || 40.7128,
    ],
    zoom: shipment.value.type == "Street" ? 15 : 11, // Street-level zoom
  });

  // Wait for map to load before geocoding
  map.value.on("load", async () => {
    await initializeCoordinates();
    if (trackingNumber.value) {
      updateParcelLocation();
    }
  });
});

// Start tracking simulation
const startTracking = async () => {
  console.log("started");
  if (!map.value) {
    console.error("Map not initialized");
    return;
  }
  if (!trackingNumber.value) {
    trackingNumber.value = generateTrackingNumber();
    parcel.value.id = trackingNumber.value;
  }
  await updateParcelLocation();
};

// Update parcel location and route
const updateParcelLocation = async () => {
  const location = streetAddresses.value[currentLocationIndex.value];
  if (!location?.lat || !location?.lng) {
    const coords = await geocodeAddress(location.name);
    if (coords) {
      location.lat = coords.lat;
      location.lng = coords.lng;
    } else {
      console.error("Failed to geocode address:", location.name);
      return;
    }
  }

  parcel.value = {
    id: trackingNumber.value,
    status:
      currentLocationIndex.value < streetAddresses.value.length - 1
        ? "In Transit"
        : "Delivered",
    address: location.name,
    estimatedDelivery: parcel.value.estimatedDelivery,
    location: { lat: location.lat, lng: location.lng },
  };

  if (parcel.value.location && map.value) {
    map.value.setCenter([parcel.value.location.lng, parcel.value.location.lat]);
    const markerId = "parcel-marker";
    if (document.getElementById(markerId)) {
      document.getElementById(markerId).remove();
    }
    new mapboxgl.Marker({ element: createMarkerElement() })
      .setLngLat([parcel.value.location.lng, parcel.value.location.lat])
      .setPopup(
        new mapboxgl.Popup().setHTML(`
          <h3>Parcel #${shipment.value?.id || parcel.value.id}</h3>
          <p>Status: ${shipment.value?.status || parcel.value.status}</p>
          <p>Location: ${shipment.value?.location || parcel.value.address}</p>
          <p>Estimated Delivery: ${
            shipment.value?.dueDate || parcel.value.estimatedDelivery
          }</p>
        `)
      )
      .addTo(map.value);
  }

  updateRoute();
  currentLocationIndex.value =
    (currentLocationIndex.value + 1) % streetAddresses.value.length;
};

// Create custom marker
const createMarkerElement = () => {
  const el = document.createElement("div");
  el.id = "parcel-marker";
  el.style.backgroundColor = "#ea580c";
  el.style.width = "20px";
  el.style.height = "20px";
  el.style.borderRadius = "50%";
  el.style.border = "2px solid #FFFFFF";
  return el;
};

// Update route on map
const updateRoute = () => {
  if (!map.value) return;
  const coordinates = streetAddresses.value
    .slice(0, currentLocationIndex.value + 1)
    .filter((loc) => loc.lat && loc.lng)
    .map((loc) => [loc.lng, loc.lat]);

  if (map.value.getSource(routeLayerId)) {
    map.value.getSource(routeLayerId).setData({
      type: "Feature",
      geometry: { type: "LineString", coordinates },
    });
  } else {
    map.value.addSource(routeLayerId, {
      type: "geojson",
      data: { type: "Feature", geometry: { type: "LineString", coordinates } },
    });
    map.value.addLayer({
      id: routeLayerId,
      type: "line",
      source: routeLayerId,
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#ea580c", "line-width": 4 },
    });
  }
};

// Simulate movement every 10 seconds
const startSimulation = () => {
  const interval = setInterval(updateParcelLocation, 10000); // Every 10 seconds
  return () => clearInterval(interval);
};

// Trigger simulation on button click
onMounted(() => {
  let interval;
  return () => {
    if (interval) clearInterval(interval);
  };
});

const startTrackingAndSimulation = async () => {
  await startTracking();
  startSimulation();
};

// Cleanup
onUnmounted(() => {
  if (map.value) map.value.remove();
});

defineExpose({
  startTracking,
});
</script>

<style>
@import "mapbox-gl/dist/mapbox-gl.css";
</style>
